/* eslint-disable complexity */
/* eslint-disable max-lines */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { useContext } from 'react';
import { Axis, BlueprintInstanceType, Components, type CornerSize, COUNTERTOP_DEFAULT_OVERHANG, COUNTERTOP_NONE_KEY, COUNTERTOP_TEXTURE_DIMENSION, IslandType, Vertex3 } from '@rta/rta-blueprint-library';

import { addContextMenu } from '../../context-menu';
import { AppContext } from '@r-app/app-context';
import { ALWAYS_VISIBLE_NO_SKU } from '../helpers/sku-labels';
import { DimensionLine } from '@r-components';
import { DisplayMode } from '@r-core';
import { NON_APPLIANCE_CATEGORY_KEYS } from '../../context-menu/helpers/is-swappable-appliance';
import { NON_COUNTERTOP_PLACEHOLDER_TEXTURE, NON_COUNTERTOP_TINT_COLOR, NON_COUNTERTOP_TINT_OPACITY } from './render-countertop';
import { RenderBreakpointsCountertop } from './render-breakpoints-countertop';
import { RenderCountertopMask } from './render-countertop-mask';
import { renderTracking } from '@r-services/render-tracking';
import { showCountertopDil } from '../show/show-countertop.dil';
import { svgPolygonFromVectors } from '../helpers/svg-polygon-from-vectors';
import { usePanel } from '@r-hooks';
import { AddIslandActions, IslandActions } from '../../components/action-menu';
import { convertPolyPoints2Vectors, convertRect2Vectors } from '@r-core/helpers';
import { Layer, ShowDimensions } from '@r-core/types';

import '../../app/styles/blueprint-component.scss';
import { RenderOverheadPanelItemGroups } from './render-overhead-panel-item-groups';
import { RenderBacksplashPanelsOverhead } from './render-backsplash-panels-overhead';
import { RenderOverhangRemovalLine } from './render-overhang-removal-line';

export type RenderIslandOverheadProps = {
	islandId: string;
	offsetX?: number;
	offsetY?: number;
	renderDimensions?: 'all' | 'skip' | 'only';
};

export const RenderIslandOverhead = (props: RenderIslandOverheadProps): React.ReactElement => {
	const {
		controller
	} = useContext(AppContext);
	const { options, kitchen, navigation } = controller;

	const kitchenSize = kitchen.getSize(options.showCountertops);
	const { islandId, offsetX = 0, offsetY = 0, renderDimensions = 'all' } = props;
	const island = kitchen.design.islands.findById(islandId);

	if (!island) {
		console.error(`Island with id ${islandId} not found`);
		return <></>;
	}

	const viewportRotation = controller.viewport?.config?.rotation ?? 0;
	const islandParentRotation = viewportRotation + island.axis.y;

	const { frontPanelId } = island.panels;
	const [{ panel: frontPanel }, panelMeta] = usePanel({ panelId: frontPanelId, isCountertop: true });
	const devInsightsLayer = showCountertopDil({ island, options });
	const hasBreakPoints = panelMeta.breakpoints.length > 0;

	let verticalBreakpoints = [];
	let sidePlaced = false;

	const isDepthOriented = (axisAngle: number): boolean => !(Math.abs(axisAngle % 180) < 45 || Math.abs(axisAngle % 180) > 135);
	// prefer the depth side whose persisted inside record is visible so the OL page matches the designer's toggle-on view; force the first side only when every record is hidden
	const depthPanels = island.panels.children.filter((p) => isDepthOriented(p.getOriginAngle()));
	const visibleDepthPanel = depthPanels.find((p) => {
		const record = kitchen.design.dimensions.children.find((dim) => dim.sourceId === `${p.id}:inside` && dim.targetId === island.id);
		return !record || record.isVisible;
	});
	const insideDepthPanelId = (visibleDepthPanel ?? depthPanels[0])?.id ?? null;

	// surround texture — an island without a countertop must render its panels, not crash the design
	const materialAsset = panelMeta.countertop?.material ?? null;
	const isNonCountertop = !materialAsset || materialAsset.key === COUNTERTOP_NONE_KEY;
	const effectiveTexture = isNonCountertop ? NON_COUNTERTOP_PLACEHOLDER_TEXTURE : materialAsset.texture;
	const materialScale = {
		height: (materialAsset?.scale.height ?? 1) * COUNTERTOP_TEXTURE_DIMENSION,
		width: (materialAsset?.scale.width ?? 1) * COUNTERTOP_TEXTURE_DIMENSION
	};

	const blueprintToApplianceOpacity = (100 - options.applianceOpacity) / 100;

	const underlyingPanels = island.panels.children.map((panel) => {
		const underlyingPanel = {
			panel,
			connections: {
				isTarget: (island.connections.children.find((connection) => {
					return !connection.isSource && connection.sourcePanelId === panel.id;
				}) ?? null) !== null,
				isSource: (island.connections.children.find((connection) => {
					return connection.isSource && connection.sourcePanelId === panel.id;
				}) ?? null) !== null
			},
			vectors: convertRect2Vectors({
				layer: Layer.Panel,
				center: { x: 0, y: 0 },
				width: panel.size.width,
				height: panel.thickness,
				angle: 0
			})
		};

		return underlyingPanel;
	}).filter((panelData) => {
		// Filter out panels with materials that are bottom-only
		type MaterialWithViewType = {
			viewType?: 'bottom' | 'bottomOnly' | 'overhead' | 'top';
			bottomOnly?: boolean;
			viewTypes?: string[];
		};

		const material = island.panels.surroundMaterial as MaterialWithViewType;

		// Check for viewType property (e.g., 'bottom', 'bottomOnly')
		if ('viewType' in material && material.viewType !== undefined) {
			return material.viewType !== 'bottom' && material.viewType !== 'bottomOnly';
		}
		// Check for bottomOnly boolean property
		if ('bottomOnly' in material && material.bottomOnly !== undefined) {
			return !material.bottomOnly;
		}
		// Check for viewTypes array that should include 'overhead' or 'top'
		if ('viewTypes' in material && material.viewTypes !== undefined && Array.isArray(material.viewTypes)) {
			return material.viewTypes.includes('overhead') || material.viewTypes.includes('top');
		}
		// Default: show the panel if no view restriction is specified (backward compatible)
		return true;
	});

	// countertop
	const countertop = island.getCountertop();
	const countertopVectors = countertop ? convertPolyPoints2Vectors(countertop.renderingDetails) : [];
	const translate = {
		x: offsetX,
		y: offsetY
	};

	let showTexture = true;
	let includeLabelBox = true;
	let useDimensionBox = true;
	let { showAppliances, showCutouts, showBacksplashes } = options;
	const { showCorners, showApplianceDimensions } = options;
	const { showPanelOutsideDimensions, showCountertops } = options;

	let showPanelItemGroups = false;
	switch (navigation.displayMode) {
		case DisplayMode.OverheadManufacturing:
			showTexture = false;
			showAppliances = false;
			showBacksplashes = false;
			includeLabelBox = false;
			useDimensionBox = false;
			break;
		case DisplayMode.SkuOverheadManufacturing:
			showTexture = false;
			showAppliances = false;
			showCutouts = false;
			includeLabelBox = false;
			useDimensionBox = false;
			showPanelItemGroups = true;
			break;
		case DisplayMode.SkuOverhead:
			showAppliances = false;
			showCutouts = false;
			showPanelItemGroups = true;
			break;
		default:
	}
	showPanelItemGroups = showPanelItemGroups || showAppliances || showCutouts;

	// For SkuOverheadManufacturing, collect IDs of hidden panel items to filter intersection dimensions
	const hiddenPanelItemIds = new Set<string>();
	if (navigation.displayMode === DisplayMode.SkuOverheadManufacturing) {
		frontPanel.panelItemGroups.children.forEach((group) => {
			group.items.children.forEach((pi) => {
				const isCustomTopOnly = pi.item.sku === 'CUSTOM' && pi.item.placement === Components.PLACEMENT_TOP_ONLY;
				const isAlwaysVisible = ALWAYS_VISIBLE_NO_SKU.includes(pi.item.label) || isCustomTopOnly;
				if (!isAlwaysVisible) {
					hiddenPanelItemIds.add(pi.id);
				}
			});
		});
	}

	const horizontalIntersects = (countertop ? frontPanel.getHorizontalIntersectionAreas({ inside: !showAppliances, includeDuplicates: false, includeEnd2End: false, minHeight: 0.5, isCountertop: true }) : [])
		.map((area) => ({
			...area,
			shouldHide: false
			// dimension: kitchen.design.dimensions.find(area.InterectionReferenceIds, area.axis, true) as Dimension
		}));

	const verticalIntersects = (countertop ? frontPanel.getVerticalIntersectionAreas({ inside: !showAppliances, includeDuplicates: false, includeEnd2End: false, minWidth: 0.5, isCountertop: true }) : [])
		.map((area) => ({
			...area,
			shouldHide: false
			// dimension: kitchen.design.dimensions.find(area.InterectionReferenceIds, area.axis, true) as Dimension
		}));

	let intersectionAreas = horizontalIntersects.concat(verticalIntersects);

	horizontalIntersects.forEach((hi) => {
		const hLeft = hi.origin.x - (hi.size.width / 2);
		const hRight = hi.origin.x + (hi.size.width / 2);
		const hTop = hi.origin.y - (hi.size.height / 2);
		const hBottom = hi.origin.y + (hi.size.height / 2);
		const matched = verticalIntersects.find((vi) => {
			const vLeft = vi.origin.x - (vi.size.width / 2);
			const vRight = vi.origin.x + (vi.size.width / 2);
			const vTop = vi.origin.y - (vi.size.height / 2);
			const vBottom = vi.origin.y + (vi.size.height / 2);
			return (hLeft <= vLeft) && (hRight >= vRight) && (hTop >= vTop) && (hBottom <= vBottom);
		});

		if (matched) {
			hi.shouldHide = true;
		}
	});

	// Plain SkuOverhead: keep only cutout gaps (cutout-to-edge / cutout-to-cutout); appliance gaps stay hidden.
	if (navigation.displayMode === DisplayMode.SkuOverhead) {
		const resolveRefCutout = (refId: string | null): { isItem: boolean; isPlainCutout: boolean } => {
			if (!refId || typeof refId !== 'string') return { isItem: false, isPlainCutout: false };
			const refItem = kitchen.getReferenceMap(refId)?.panelItem;
			if (!refItem?.item) return { isItem: false, isPlainCutout: false };
			const isPlainCutout = NON_APPLIANCE_CATEGORY_KEYS.has(refItem.item.category?.key ?? '')
				&& refItem.item.sku !== 'CUSTOM'
				&& !refItem.item.override;
			return { isItem: true, isPlainCutout };
		};
		intersectionAreas = intersectionAreas.filter((area) => {
			const states = (area.intersectionReferenceIds ?? []).map(resolveRefCutout);
			return states.some((s) => s.isPlainCutout) && !states.some((s) => s.isItem && !s.isPlainCutout);
		});
	}

	renderTracking.incrementRender('ShowOverhead');
	return (<g key={`${islandId}-overhead`} transform={`translate(${translate.x}, ${translate.y})`}>
		{renderDimensions !== 'only' && <>
		{ showCountertops && countertop !== null && <>
			{ /* Create countertop texture def */ }
			{ showTexture && <defs>
				<pattern id="counter-texture" x="0" y="0" height={materialScale.height} width={materialScale.width} patternUnits="userSpaceOnUse">
					<image href={`${options.assetsBaseUrl}/textures/${effectiveTexture}${options.assetsSasToken}`} x="0" y="0" width={materialScale.width} height={materialScale.height} />
				</pattern>
			</defs>}

			{ /* create cutout mask for countertop */ }
			{(showCutouts) &&
				<RenderCountertopMask islandId={islandId} />
			}

			{ /* Draw countertop */ }
			{ svgPolygonFromVectors(countertopVectors, {
				key: 'countertop-context',
				fill: 'darkgray',
				stroke: 'lightgrey',
				strokeWidth: '.1',
				opacity: blueprintToApplianceOpacity === 0 ? 1 : 0.65,
				...(showCutouts ? { mask: `url(#countertop-mask-${islandId})` } : {}),
				...addContextMenu({ menuKey: BlueprintInstanceType.Countertop, id: panelMeta.countertop.id })
			})}
		</> }

		{ /* Draw hidden countertop layer for context menu */ }
		{ !showCountertops && countertop !== null && <>
			{ svgPolygonFromVectors(countertopVectors, {
				key: 'countertop-context',
				opacity: 0,
				...addContextMenu({ menuKey: BlueprintInstanceType.Countertop, id: island.id })
			})}
		</> }

		{ /* Draw Countertop Texture */ }
		{ showCountertops && showTexture && countertop !== null &&
			<>{ svgPolygonFromVectors(countertopVectors, {
				key: 'countertop',
				stroke: 'none',
				fill: 'url(#counter-texture)',
				mask: `url(#countertop-mask-${islandId})`,
				opacity: blueprintToApplianceOpacity,
				...addContextMenu({ menuKey: BlueprintInstanceType.Countertop, id: panelMeta.countertop.id })
			})}</>
		}

		{ /* Red tint marking a non-RTA placeholder countertop */ }
		{ showCountertops && showTexture && isNonCountertop &&
			<>{ svgPolygonFromVectors(countertopVectors, {
				key: 'countertop-non-rta-tint',
				stroke: 'none',
				fill: NON_COUNTERTOP_TINT_COLOR,
				mask: `url(#countertop-mask-${islandId})`,
				opacity: NON_COUNTERTOP_TINT_OPACITY,
				...addContextMenu({ menuKey: BlueprintInstanceType.Countertop, id: panelMeta.countertop.id })
			})}</>
		}


		{
			underlyingPanels.map((panelData) => {
				const panelCenter = panelData.panel.center;
				return <g
					key={panelData.panel.id}
					type="island-panel"
					transform={`translate(${panelCenter.x}, ${panelCenter.z}) rotate(${-panelData.panel.getOriginAngle()})`}
				>{
					svgPolygonFromVectors(
						panelData.vectors,
						{
							key: panelData.panel.id,
							fill: 'lightgrey',
							stroke: 'black',
							strokeWidth: '.0625',
							strokeDasharray: '2 .5',
							opacity: options.showCountertops ? 0.25 : 0.75,
							...addContextMenu({ menuKey: BlueprintInstanceType.Panel, id: panelData.panel.id, center: { x: panelCenter.x, y: panelCenter.z } })
						}
					) }

					{ /* display Appliances */}
					{showPanelItemGroups &&
						<RenderOverheadPanelItemGroups panelItemGroups={panelData.panel.panelItemGroups} />
					}
				</g>
			})
		}

		{ /* Render backsplash panels */ }
		<RenderBacksplashPanelsOverhead countertop={countertop} island={island} showCountertops={showCountertops} showBacksplashes={showBacksplashes} />

		{ /* show corners */ }
		{ showCorners &&
			<g fill="lightgray" opacity={options.showCountertops ? 0.55 : 1}>
				{ island.panelTrims.children.map((panelTrim) => {
					const { renderingDetails: ptRenderingDetails, center: panelTrimCenter } = panelTrim;
					return svgPolygonFromVectors(convertPolyPoints2Vectors(ptRenderingDetails), {
						key: panelTrim.id,
						transform: `translate(${panelTrimCenter.x}, ${panelTrimCenter.z})`,
						...addContextMenu({ menuKey: BlueprintInstanceType.PanelTrim, id: panelTrim.id })
					});
				})}
			</g>
		}

		{ /* render breakpoints */ }
		{ options.showBreakpoints &&
			<RenderBreakpointsCountertop {...{ islandId: island.id }} />
		}
		</>}

		{renderDimensions !== 'skip' && <>
		{ /* add panel outside dimensions */ }
		{ showPanelOutsideDimensions && island.panels.children.map((panel, i) => {
			// Check if panel has connections to non-obstruction islands
			const hasNonObstructionConnections = (navigation.displayMode === DisplayMode.Overhead || navigation.displayMode === DisplayMode.OverheadManufacturing || navigation.displayMode === DisplayMode.SkuOverhead || navigation.displayMode === DisplayMode.SkuOverheadManufacturing) &&
				(() => {
					const sourceConnections = island.connections.getConnectionsBySourcePanelId(panel.id);
					const hasIslandSourceConnection = sourceConnections.some((conn) => conn.targetIsland?.instanceOf === BlueprintInstanceType.Island);

					const targetConnection = island.connections.findConnectionsByTargetIslandId(panel.id);
					const hasIslandTargetConnection = targetConnection?.sourceIsland?.instanceOf === BlueprintInstanceType.Island;

					return hasIslandSourceConnection || hasIslandTargetConnection;
				})();
			if (hasNonObstructionConnections) return null;

			// const { corners, position } = panel.getCornerPoints(true);
			const overhangOverride = showCountertops ? undefined : COUNTERTOP_DEFAULT_OVERHANG;
			const dimOverhang = overhangOverride ?? panel.countertopOverhang;
			const { corners, center } = panel.getSidePoints({ includeCountertop: true, overhangOverride, translate: true, rotate: true });
			let length = corners[0].getDistance(corners[1]);
			const cornerLength = corners[2].getDistance(corners[3]);

			// Corner island dimension override: processSideConnection produces inconsistent
			// padding at 45° miter joints (uses adjacent panel's thickness which differs per
			// side of a joint). All five panel dimensions are computed from known geometry
			// instead: back panels from structural width + overhangs, side panels from
			// cornerSize.depth + overhangs, front panel from the Pythagorean relationship
			// between the back and side dimensions (the hypotenuse of the corner triangle).
			if (island.type === IslandType.Corner) {
				const cornerSize = island.size as CornerSize;
				const oh = showCountertops ? null : COUNTERTOP_DEFAULT_OVERHANG;
				if (panel.isFrontPanel) {
					// The front edge of the corner island countertop is the hypotenuse
					// of the right triangle formed by the back and side dimensions.
					// Compute back/side lengths using the same override formulas, then Pythagoras.
					const leftSide = frontPanel.getLeftPanel();
					const rightSide = frontPanel.getRightPanel();
					const backWidth = Math.max(...island.panels.getBackPanels().map((p) => p.size.width));
					const leftBack = leftSide?.getLeftPanel();
					const rightBack = rightSide?.getRightPanel();
					const frontOh = oh ?? frontPanel.countertopOverhang ?? 0;
					const leftBackLen = backWidth + (oh ?? leftBack?.getLeftPanel()?.countertopOverhang ?? 0) + (oh ?? leftBack?.getRightPanel()?.countertopOverhang ?? 0);
					const rightBackLen = backWidth + (oh ?? rightBack?.getLeftPanel()?.countertopOverhang ?? 0) + (oh ?? rightBack?.getRightPanel()?.countertopOverhang ?? 0);
					const leftSideLen = cornerSize.depth.left + (oh ?? leftSide?.getLeftPanel()?.countertopOverhang ?? 0) + frontOh - (oh ?? leftSide?.countertopOverhang ?? 0);
					const rightSideLen = cornerSize.depth.right + (oh ?? rightSide?.getRightPanel()?.countertopOverhang ?? 0) + frontOh - (oh ?? rightSide?.countertopOverhang ?? 0);
					const dx = leftBackLen - rightSideLen;
					const dy = rightBackLen - leftSideLen;
					length = Math.sqrt(dx * dx + dy * dy);
				} else if (panel.isBackPanel) {
					const leftAdj = panel.getLeftPanel();
					const rightAdj = panel.getRightPanel();
					// Both back panels share the same countertop width. The right back panel
					// is shorter by backThickness (Inside/Outside joint at the apex), so use
					// the max structural width across both back panels.
					const backWidth = Math.max(...island.panels.getBackPanels().map((p) => p.size.width));
					length = backWidth + (oh ?? leftAdj?.countertopOverhang ?? 0) + (oh ?? rightAdj?.countertopOverhang ?? 0);
				} else if (panel.isLeftPanel) {
					const backOverhang = oh ?? panel.getLeftPanel()?.countertopOverhang ?? 0;
					const frontOverhang = oh ?? frontPanel.countertopOverhang;
					const sideOverhang = oh ?? panel.countertopOverhang;
					length = cornerSize.depth.left + backOverhang + frontOverhang - sideOverhang;
				} else if (panel.isRightPanel) {
					const backOverhang = oh ?? panel.getRightPanel()?.countertopOverhang ?? 0;
					const frontOverhang = oh ?? frontPanel.countertopOverhang;
					const sideOverhang = oh ?? panel.countertopOverhang;
					length = cornerSize.depth.right + backOverhang + frontOverhang - sideOverhang;
				}
			}

			// Use panel center for positioning instead of position property
			const panelCenter = panel.center;
			const panelAxis = panel.getOriginAngle();
			const isLevel = Math.abs(panelAxis % 180) < 45 || Math.abs(panelAxis % 180) > 135;

			// add breakpoint dimensions
			if (hasBreakPoints && options.showBreakpoints && i === 2) {
				const bpList = countertop ? panelMeta.countertop.breakpoints : frontPanel.breakpoints;
				let prevBpPosition = corners[1].x;
				let prevBpId = panel.id;
				verticalBreakpoints = [...bpList.children].sort((a, b) => a.center - b.center).map((bp) => {
					const bpd = {
						width: bp.center - prevBpPosition,
						xOffset: prevBpPosition,
						sourceId: prevBpId,
						targetId: bp.id
					};
					prevBpPosition += bpd.width;
					prevBpId = bp.id;

					return bpd;
				});

				// add end dimension
				verticalBreakpoints.push({
					width: length - prevBpPosition + corners[1].x,
					xOffset: prevBpPosition,
					yOffset: prevBpPosition,
					sourceId: prevBpId,
					targetId: panel.id
				});
				// console.log('vbp:end', length, prevBpPosition, corners[1].x);
				return <g key={`breakpoint-${i}`}>
					<DimensionLine
						length={length}
						persist={{ sourceId: panel.id, targetId: island.id }}
						axis={new Axis({ dataIn: { x: 0, y: panelAxis, z: 0 }})}
						includeLabelBox={includeLabelBox}
						topView={true}
						bBox={{
							origin: panelCenter,
							size: { width: length, height: length, depth: length }
						}}
						padding={(dimOverhang) + (panel.thickness / 2) + 5.5}
						indicatorStart="flat" indicatorEnd="flat"
						canMove={true}
						dragConstraint="1d"
						parentRotation={islandParentRotation}
					/>

					{/* render breakpoint dimensions */}
					{verticalBreakpoints.map((vbp, iBp) => {
						return <DimensionLine key={iBp}
							length={vbp.width}
							persist={{ sourceId: vbp.sourceId, targetId: vbp.targetId }}
							axis={new Axis({ dataIn: { x: 0, y: panelAxis, z: 0 }})}
							topView={true}
							bBox={{
								origin: new Vertex3({
									dataIn: {
										x: vbp.xOffset + (vbp.width / 2),
										y: panelCenter.y,
										z: panelCenter.z
									}
								}),
								size: { width: vbp.width, height: vbp.height, depth: 0 }
							}}

							includeLabelBox={includeLabelBox}
							padding={(dimOverhang) + (panel.thickness / 2) + 3}
							indicatorStart="flat" indicatorEnd="flat"
							canMove={true}
							dragConstraint="1d"
							parentRotation={islandParentRotation}
						/>
					})}
				</g>
			}

			const isCornerIsland = island.type === IslandType.Corner;
			if (isCornerIsland || !panel.isFrontPanel) {
				// Draw only one of the two equal side panels (the base depth); gate on panel role, not array index, since persisted panel order is not guaranteed after save/reload.
				if (!isCornerIsland && panel.isSidePanel) {
					if (sidePlaced) return null;
					sidePlaced = true;
				}

				let flipLabel = false;
				if (((panelAxis === 90 && island.axis.y !== 90) || (panelAxis === 270 && island.axis.y !== 270))) {
					flipLabel = true;
				}

				return <DimensionLine key={i}
					length={length}
					persist={{ sourceId: panel.id, targetId: island.id }}
					axis={new Axis({ dataIn: { x: 0, y: panelAxis, z: 0 }})}
					includeLabelBox={includeLabelBox}
					topView={true}
					bBox={{
						origin: center,
						size: { width: length, height: length, depth: length }
					}}
					padding={(dimOverhang / 2) + 1.75}
					indicatorStart="flat" indicatorEnd="flat"
					canMove={true}
					islandAngle={-island.axis.y}
					flipLabelText={flipLabel}
					dragConstraint="1d"
					parentRotation={islandParentRotation}
				/>
			}
			return null;
		})}

		{ /* add panel inside dimensions */ }
		{ options.showPanelInsideDimensions && island.panels.children.map((panel, i) => {
			const panelAxis = panel.getOriginAngle();

			// Select the depth side by geometry (panel axis), not role getters -- a depth-bearing side can misclassify as a back panel after save/reload, which hides the inside depth entirely.
			if (navigation.displayMode === DisplayMode.OverheadManufacturing && island.type !== IslandType.Corner) {
				const isWidthOriented = !isDepthOriented(panelAxis);
				if (isWidthOriented && !panel.isFrontPanel) return null;
				if (!isWidthOriented && panel.id !== insideDepthPanelId) return null;
			}

			// Use getSidePoints to get total side base width (without countertop overhang for inside dimensions)
			const { corners, center } = panel.getSidePoints({ includeCountertop: false, translate: true, rotate: true });
			let length = corners[0].getDistance(corners[1]);

			// Corner island inside dimension override: same rationale as outside dimensions.
			// The 45° joints produce irrational padding; use exact structural values instead.
			if (island.type === IslandType.Corner) {
				const cornerSize = island.size as CornerSize;
				if (panel.isFrontPanel) {
					length = panel.size.width;
				} else if (panel.isBackPanel) {
					length = panel.size.width;
				} else if (panel.isLeftPanel) {
					length = cornerSize.depth.left;
				} else if (panel.isRightPanel) {
					length = cornerSize.depth.right;
				}
			}

			// For non-corner islands, position the dimension halfway between panel edge and island center
			const isCorner = island.type === IslandType.Corner;
			const dimensionOrigin = isCorner
				? center
				: new Vertex3({
					dataIn: {
						x: center.x * 0.5,
						y: center.y,
						z: center.z * 0.5
					}
				});

			return <DimensionLine key={i}
				persist={{ sourceId: `${panel.id}:inside`, targetId: island.id }}
				ignoreHidden={navigation.displayMode === DisplayMode.OverheadManufacturing}
				length={length}
				axis={new Axis({ dataIn: { x: 0, y: panelAxis, z: 0 }})}
				includeLabelBox={includeLabelBox}
				topView={true}
				bBox={{
					origin: dimensionOrigin,
					size: { width: length, height: length, depth: length }
				}}
				padding={0}
				indicatorStart="arrow" indicatorEnd="arrow"
			/>
		})}

		{ /* add panel appliance dimensions */ }
		{ options.showDimensionsMode !== ShowDimensions.None && showApplianceDimensions && intersectionAreas
			.filter((area) => {
				if (area.intersectionReferenceIds.some((id) => hiddenPanelItemIds.has(id))) return false;
				// OverheadManufacturing: top-only items that don't cut into the countertop aren't drawn here, so drop their gaps.
				if (navigation.displayMode === DisplayMode.OverheadManufacturing && area.intersectionReferenceIds.some((refId) => {
					if (!refId || typeof refId !== 'string') return false;
					const refItem = kitchen.getReferenceMap(refId)?.panelItem;
					return refItem?.item?.placement === Components.PLACEMENT_TOP_ONLY && !refItem?.item?.distanceBelowCountertop;
				})) return false;
				return true;
			})
			.map((area, i) => {
			// console.log('intersectionArea:render', { labelFactor: area.dimension.labelFactor, lineFactor: area.dimension.lineFactor });
			// console.log('width', area.size.width  ,'sourceId', area.dimension?.sourceId, '||',

			// Suffix the persist sourceId so the overhead (top-down) intersection dim resolves to a separate Dimension from the front-panel view -- otherwise hiding the label in the front-panel view also hides it here. Mirrors the per-item suffix in draw-panel-item-dimensions.tsx.
			return <DimensionLine key={i}
				persist={{ sourceId: `${area.intersectionReferenceIds[0]}:ct`, targetId: area.intersectionReferenceIds[1], alternateId: area.intersectionReferenceIds[2] }}
				axis={area.axis}
				// labelFactor={area.dimension?.labelFactor}
				// lineFactor={area.dimension?.lineFactor}
				length={area.axis.z === 0 ? area.size.width : area.size.height}
				canMove={true}
				// onMove={(data: UseDraggableSvgPosition) => {
				// 	area.dimension.labelFactor = area.axis.z === 0 ? data.factors.x : data.factors.y;
				// 	area.dimension.lineFactor = area.axis.z === 0 ? data.factors.x : data.factors.y;
				// }}
				bBox={{ origin: area.origin, size: area.size }}
				includeLabelBox={useDimensionBox}
				level={true}
				shouldHide = {area.shouldHide}
				islandAngle={-island.axis.y}
				dragConstraint="2d"
				parentRotation={islandParentRotation}
			/>;
		})}
		</>}

		{renderDimensions !== 'only' && <>
		{ /* render add island buttons when in overhead mode */ }
		<AddIslandActions islandId={island.id} offset={{ x: offsetX, y: offsetY }}/>

		{/* render delete island buttons when in overhead mode */ }
		<IslandActions islandId={island.id} offset={{ x: offsetX, y: offsetY }}/>

		{/* Red overhang removal line - only rendered in SKU mode (check is internal) */}
		<RenderOverhangRemovalLine island={island} />


		{ devInsightsLayer.islandOrigin }
		{ devInsightsLayer.connectionPoints }
		{ devInsightsLayer.cornerPoints }
		</>}
	</g>);
};
